import { FC, useEffect, useMemo, useRef } from 'react';

import { getSelectedProfessions } from 'utils/profRoles';

import generateImageModalFiltersFromSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_design/generate_image_modal_filters_form_submit';
import { ActionBar, BottomSheet, Button, Modal, NavigationBar, Text, useBreakpoint } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24, HyperstarSparkleFilledSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { fetchRemainingAiImageGenerations } from 'components/common/BannerImg/AiImageGenerationModal/fetchRemainingAiImageGenerations';
import { GeneratedImages } from 'components/common/BannerImg/AiImageGenerationModal/GeneratedImages/GeneratedImages';
import { generateImage } from 'components/common/BannerImg/AiImageGenerationModal/generateImage';
import { GenerationSelectors } from 'components/common/BannerImg/AiImageGenerationModal/GenerationSelectors/GenerationSelectors';
import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { selectProfessions } from 'store/autoReducers';
import { setIsAiGenerationModalVisible } from 'store/models/aiImageGeneration';
import { setIsAiImageGenerationModalOpenedFromGalleryModal, setIsGalleryModalVisible } from 'store/models/gallery';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/AiImageGenerationModal/AiImageGenerationModal.module.less';

const Trls = {
    title: 'AdCreate.AdForm.BannerImg.generation.modal.title',
    subtitle: 'AdCreate.AdForm.BannerImg.generation.modal.subtitle',
    remainingGenerations: 'AdCreate.AdForm.BannerImg.generation.modal.remainingGenerations',
    noRemainingGenerations: 'AdCreate.AdForm.BannerImg.generation.modal.noRemainingGenerations',
    cancel: 'AdCreate.AdForm.BannerImg.generation.modal.cancel',
    generate: 'AdCreate.AdForm.BannerImg.generation.modal.generate',
};

const AiImageGenerationModalComponent: FC<{ isQuickCampaign?: boolean }> = ({ isQuickCampaign }) => {
    const { isAiImageGenerationModalOpenedFromGalleryModal } = useSelector((state) => state.gallery);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    const modalRef = useRef<HTMLDivElement>(null);

    const scrollTo = () => {
        if (!modalRef?.current) {
            return;
        }
        // скролл контейнеры у модалки и боттом шита отличаются, поэтому проверям оба
        const container =
            modalRef.current.querySelector('[class*="scroll-container"]') ||
            modalRef.current.querySelector('[data-qa="modal-content-scroll-container"]');

        if (!container) {
            return;
        }

        container.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const { isAiImageGenerationModalVisible, remainingAiImageGenerations, aiImageQueueId, selectors } = useSelector(
        (state) => state.aiImageGeneration
    );
    const adCreateProfessions = useSelector((state) => state.createCampaign.adProfessions);
    const adQuickProfessions = useSelector((state) => state.createQuickCampaign.profRoles);

    const adProfessions = isQuickCampaign ? adQuickProfessions : adCreateProfessions;

    const profRoles = useSelector(selectProfessions);

    const profRole = useMemo(() => {
        const [firstProfession] = getSelectedProfessions(profRoles, adProfessions);
        return firstProfession?.name;
    }, [adProfessions, profRoles]);

    const onClose = () => {
        dispatch(setIsAiGenerationModalVisible(false));

        if (isAiImageGenerationModalOpenedFromGalleryModal) {
            dispatch([setIsAiImageGenerationModalOpenedFromGalleryModal(false), setIsGalleryModalVisible(true)]);
        }
    };

    useEffect(() => {
        void dispatch(fetchRemainingAiImageGenerations());
    }, [dispatch]);

    const subtitle = (
        <>
            <Text Element="span" style="secondary" typography={isMobile ? 'label-4-regular' : 'paragraph-1-regular'}>
                {trls(Trls.subtitle)}
            </Text>
            <Text
                Element="span"
                style={isMobile ? 'secondary' : 'primary'}
                typography={isMobile ? 'label-4-regular' : 'paragraph-1-regular'}
            >
                {profRole}
            </Text>
        </>
    );

    const actions = <CrossOutlinedSize24 onClick={onClose} initialColor="secondary" />;

    const remainingGenerationsInfo = (
        <div className={styles.remainingGenerationsInfo}>
            {remainingAiImageGenerations > 0 ? (
                <Text>{trls(Trls.remainingGenerations, [remainingAiImageGenerations])}</Text>
            ) : (
                <Text>{trls(Trls.noRemainingGenerations)}</Text>
            )}
        </div>
    );

    const footer = (
        <ActionBar
            secondaryActions={!isMobile && remainingGenerationsInfo}
            additionalContent={isMobile && remainingGenerationsInfo}
            primaryActions={
                <>
                    {remainingAiImageGenerations > 0 && (
                        <Button
                            mode="secondary"
                            style="neutral"
                            icon={<HyperstarSparkleFilledSize24 initialColor="negative" />}
                            stretched={isMobile}
                            onClick={() => {
                                if (!aiImageQueueId) {
                                    selectors &&
                                        generateImageModalFiltersFromSubmit({
                                            ...selectors,
                                        });
                                    void dispatch(generateImage(adProfessions[0], selectors));
                                    scrollTo();
                                }
                            }}
                            data-qa="ai-generation-image-button"
                        >
                            {trls(Trls.generate)}
                        </Button>
                    )}
                </>
            }
        />
    );

    return (
        <>
            <Modal
                visible={isAiImageGenerationModalVisible}
                onClose={onClose}
                size="large"
                header={
                    <MagritteModalHeader
                        title={trls(Trls.title)}
                        subtitle={subtitle}
                        actions={actions}
                        options={<GenerationSelectors />}
                    />
                }
                footer={footer}
                ref={modalRef}
            >
                <GeneratedImages isQuickCampaign={isQuickCampaign} />
            </Modal>
            <BottomSheet
                visible={isAiImageGenerationModalVisible}
                onClose={onClose}
                height="full-screen"
                header={
                    <NavigationBar
                        title={trls(Trls.title)}
                        subtitle={subtitle}
                        right={actions}
                        options={<GenerationSelectors />}
                        showDivider="always"
                    />
                }
                footer={footer}
                showDivider="always"
                ref={modalRef}
            >
                <GeneratedImages isQuickCampaign={isQuickCampaign} />
            </BottomSheet>
        </>
    );
};

const AiImageGenerationModal = withErrorBoundary(AiImageGenerationModalComponent);
export { AiImageGenerationModal };
